<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'

import FormSection from '@/components/forms/FormSection.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import type { Supplier } from '@/types/supplier-types'
import { getSupplierList, getSuppliersByQualityId } from '@/api/suppliers'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import { getProductList } from '@/api/products'
import {FabricStatusEnum} from "@/types/product-types";
import {PricingType} from '@/constants/pricing-type'

const props = defineProps<{
  values: any
  rolls: any
  onSubmit: () => void
  setErrors: (errors: any) => void
}>()

const emit = defineEmits(['update:modelValue'])
const suppliers = ref<Supplier[] | undefined>()
const selectedRolls = ref<any>([])
const selectedQualityId = ref<string | null>(null)
const qualities = ref<any[]>([])

const selectAll = computed({
  get: () => selectedRolls.value.length === props.rolls.length,
  set: (value: boolean) => {
    if (value) {
      selectedRolls.value = props.rolls.map((roll: any) => roll.id);
    } else {
      selectedRolls.value = [];
    }
    emit('update:modelValue', selectedRolls.value);
  },
});

const handleRollSelection = (rollId: string) => {
  const index = selectedRolls.value.indexOf(rollId)
  if (index > -1) {
    selectedRolls.value.splice(index, 1)
  } else {
    selectedRolls.value.push(rollId)
  }
  emit('update:modelValue', selectedRolls.value);
};

const supplierAddresses = computed(() => {
  const selectedSupplier = suppliers.value?.find((supplier) => supplier.id === props.values.supplierId)
  return selectedSupplier ? selectedSupplier.addresses : []
})

const formattedSupplierAddress = computed(() => {
  return supplierAddresses.value.map((address: any) => ({
    ...address,
    label: [address.line1, address.line2, address.city, address.region, address.postCode, address.country]
        .filter(Boolean)
        .join(', ')
  }))
})

const fetchSuppliersByQuality = async (qualityId: string) => {
  suppliers.value = await getSuppliersByQualityId(qualityId);
}
onMounted(async () => {
  const allProducts = await getProductList()
  qualities.value = allProducts.filter(product => product.fabricStatus === FabricStatusEnum.Finished)
  suppliers.value = await getSupplierList()
  selectedRolls.value = props.rolls.map((roll: any) => roll.id);
  emit('update:modelValue', selectedRolls.value);
})

watch(() => props.values.qualityId, async (newQualityId) => {
  if (newQualityId) {
    await fetchSuppliersByQuality(newQualityId);
  } else {
    suppliers.value = [];
  }
})
const filteredSuppliers = computed(() => {
  return suppliers.value?.filter(supplier =>
    supplier.supplierPricings.some(pricing => pricing.pricingType === PricingType.Finishing)
  ) || [];
});

</script>

<template>
  <form @submit.prevent="onSubmit">
    <FormSection title="">
      <FormDropdown label="Finished Quality" inputName="qualityId" id="qualityId" :options="qualities"
        optionLabel="finishedReference" optionValue="id" />
      <FormDropdown label="Supplier" inputName="supplierId" id="supplierId" :options="filteredSuppliers" optionLabel="name"
        optionValue="id" />
      <FormDropdown label="Supplier Address" inputName="supplierAddressId" id="supplierAddressId"
        :options="formattedSupplierAddress" optionLabel="label" optionValue="id" />
      <FormInputText label="Purchase Order number" inputName="purchaseOrderNumber" id="purchaseOrderNumber" />
    </FormSection>

    <div class="mb-4">
      <p class="text-sm mb-2">Rolls to send {{ rolls.length }}</p>
      <p class="text-xs text-gray-400 mb-4">
        You can choose from the rolls within this batch <br />
        that you would like to send to the supplier
      </p>

      <div class="flex flex-row gap-2 items-center mb-4">
        <input type="checkbox" name="selectAll" id="selectAll" v-model="selectAll" />
        <label class="text-xs" for="selectAll"> Select All</label>
      </div>
      <div class="flex flex-wrap gap-8">
        <div v-for="(roll, index) in rolls" :key="index">
          <div class="flex flex-row gap-2 items-center">
            <input type="checkbox" name="roll" :id="roll.id" :checked="selectedRolls.includes(roll.id)"
              @change="handleRollSelection(roll.id)">
            <label class="text-xs" :for="roll.id"> {{ roll.rollNumber }} </label>
          </div>
        </div>
      </div>
    </div>

    <FormTextArea label="Notes" inputName="notes" id="notes" class="w-full" />
  </form>
</template>